import { useMemo, useState } from 'react';
import { Container, Form, Button, Alert, Card, Row, Col } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { contactService } from '../services/contact.service';
import { useAuth } from '../hooks/useAuth';
import { usePublicPathLocale } from '../hooks/usePublicPathLocale';
import SuccessAlert from '../components/common/SuccessAlert';
import PageMeta from '../components/common/PageMeta';
import ContactInfoCard from '../components/contact/ContactInfoCard';
import ContactFaqSection from '../components/contact/ContactFaqSection';
import ContactPageStructuredData from '../components/contact/ContactPageStructuredData';
import { formatUserName } from '../utils/name.util';
import { localePublicPagePath, SITE_URL } from '../constants/siteSeo';

const createContactSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(1, t('contact.name_required')).max(255, t('contact.name_max')),
    email: z.string().email(t('contact.email_invalid')).max(255),
    company: z.string().max(255).optional().nullable(),
    phone: z.string().max(50).optional().nullable(),
    subject: z.enum(['general', 'technical', 'billing', 'bug', 'feature', 'other'], {
      errorMap: () => ({ message: t('contact.subject_required') }),
    }),
    message: z.string().min(1, t('contact.message_required')).max(10000, t('contact.message_max')),
    agree: z.boolean().refine((val) => val === true, {
      message: t('contact.agree_required'),
    }),
  });

type ContactFormData = z.infer<ReturnType<typeof createContactSchema>>;

export default function Contact() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [success, setSuccess] = useState('');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isLoggedIn = Boolean(user);
  // `/contact` は ja / x-default 正規（/ja/contact は App ルートで Navigate）
  const pathNorm = location.pathname.replace(/\/+$/, '') || '/';
  const isBareContactPath = pathNorm === '/contact';
  const pathLocale = usePublicPathLocale();
  const contactPageUrl = `${SITE_URL}${localePublicPagePath(pathLocale, 'contact')}`;

  const contactSchema = useMemo(() => createContactSchema(t), [t, i18n.language]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: user
        ? formatUserName(user.firstName, user.lastName, i18n.language) || user.username || ''
        : '',
      email: user?.email || '',
      company: user?.organizationName || '',
      phone: '',
      subject: 'general',
      message: '',
      agree: false,
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<ContactFormData, 'agree'>) =>
      contactService.create(data as Parameters<typeof contactService.create>[0]),
    onSuccess: () => {
      setSuccess(t('contact.submit_success'));
      reset({
        name: user
          ? formatUserName(user.firstName, user.lastName, i18n.language) || user.username || ''
          : '',
        email: user?.email || '',
        company: user?.organizationName || '',
        phone: '',
        subject: 'general',
        message: '',
        agree: false,
      });
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'notice-counts'] });
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    const { agree: _agree, ...contactData } = data;
    await createMutation.mutateAsync(contactData);
  };

  const messageLength = watch('message')?.length || 0;

  return (
    <>
      <PageMeta
        titleKey="contact.title"
        descriptionKey="contact.meta_description"
        path="contact"
        localeAware={!isBareContactPath}
        localeAlternates={isBareContactPath}
        llmsDescribedBy
      />
      <ContactPageStructuredData pageUrl={contactPageUrl} />
      <Container className="mt-4 mb-5">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h1>{t('contact.title')}</h1>
            <p className="text-muted mb-0">{t('contact.subtitle')}</p>
          </div>
          <Link
            to={isLoggedIn ? '/dashboard' : '/'}
            className="btn btn-outline-secondary btn-sm"
          >
            <i className="fas fa-arrow-left me-2" />
            {t(isLoggedIn ? 'contact.back_to_dashboard' : 'contact.back_to_home')}
          </Link>
        </div>

        <Alert variant="info" className="mb-4">
          <i className="fas fa-lock me-2" />
          {t('contact.scope_note')}
        </Alert>

        {success && <SuccessAlert message={success} onDismiss={() => setSuccess('')} />}

        {createMutation.isError && (
          <Alert variant="danger" dismissible onClose={() => createMutation.reset()}>
            {createMutation.error instanceof Error
              ? createMutation.error.message.startsWith('api.error.')
                ? t(createMutation.error.message, { defaultValue: createMutation.error.message })
                : createMutation.error.message
              : t('contact.submit_failed')}
          </Alert>
        )}

        <Row>
          <Col lg={8}>
            <Card className="mb-4" id="contact-form">
              <Card.Header>
                <Card.Title className="mb-0">{t('contact.form_title')}</Card.Title>
              </Card.Header>
              <Card.Body>
                <Alert variant="light" className="border mb-4">
                  <strong>{t('contact.form_help_title')}</strong>
                  <ul className="mb-0 mt-2 small">
                    <li>{t('contact.required_fields_note')}</li>
                    <li>{t('contact.max_length_note')}</li>
                    <li>{t('register.privacy_agreement_required')}</li>
                  </ul>
                </Alert>

                <Form onSubmit={handleSubmit(onSubmit)} noValidate>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Label htmlFor="contact-name">
                        {t('contact.name')} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        id="contact-name"
                        type="text"
                        autoComplete="name"
                        {...register('name')}
                        isInvalid={!!errors.name}
                        placeholder={t('contact.name')}
                      />
                      {errors.name && (
                        <Form.Control.Feedback type="invalid">
                          {errors.name.message}
                        </Form.Control.Feedback>
                      )}
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Label htmlFor="contact-email">
                        {t('contact.email')} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        id="contact-email"
                        type="email"
                        autoComplete="email"
                        {...register('email')}
                        isInvalid={!!errors.email}
                        placeholder={t('contact.email_placeholder')}
                      />
                      {errors.email && (
                        <Form.Control.Feedback type="invalid">
                          {errors.email.message}
                        </Form.Control.Feedback>
                      )}
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Label htmlFor="contact-company">{t('contact.company')}</Form.Label>
                      <Form.Control
                        id="contact-company"
                        type="text"
                        autoComplete="organization"
                        {...register('company')}
                        placeholder={t('contact.company')}
                      />
                    </Col>
                    <Col md={6} className="mb-3">
                      <Form.Label htmlFor="contact-phone">{t('contact.phone')}</Form.Label>
                      <Form.Control
                        id="contact-phone"
                        type="tel"
                        autoComplete="tel"
                        {...register('phone')}
                        placeholder={t('contact.phone')}
                      />
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="contact-subject">
                      {t('contact.subject')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      id="contact-subject"
                      {...register('subject')}
                      isInvalid={!!errors.subject}
                    >
                      <option value="general">{t('contact.subject_general')}</option>
                      <option value="technical">{t('contact.subject_technical')}</option>
                      <option value="billing">{t('contact.subject_billing')}</option>
                      <option value="bug">{t('contact.subject_bug')}</option>
                      <option value="feature">{t('contact.subject_feature')}</option>
                      <option value="other">{t('contact.subject_other')}</option>
                    </Form.Select>
                    {errors.subject && (
                      <Form.Control.Feedback type="invalid">
                        {errors.subject.message}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="contact-message">
                      {t('contact.message')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      id="contact-message"
                      as="textarea"
                      rows={6}
                      {...register('message')}
                      isInvalid={!!errors.message}
                      placeholder={t('contact.message_placeholder')}
                    />
                    {errors.message && (
                      <Form.Control.Feedback type="invalid">
                        {errors.message.message}
                      </Form.Control.Feedback>
                    )}
                    <Form.Text className="text-muted">
                      {messageLength} {t('contact.message_counter')}
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      id="contact-agree"
                      {...register('agree')}
                      isInvalid={!!errors.agree}
                      label={
                        <>
                          <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                            {t('common.privacy_policy')}
                          </Link>{' '}
                          {t('contact.agree')} <span className="text-danger">*</span>
                        </>
                      }
                    />
                    {errors.agree && <div className="text-danger small">{errors.agree.message}</div>}
                  </Form.Group>

                  <Button variant="primary" type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? t('contact.submitting') : t('contact.submit')}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            <ContactInfoCard />
            <ContactFaqSection />
          </Col>
        </Row>
      </Container>
    </>
  );
}
